import { useEffect, useState } from "react";
import { Loader2, MailCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const TOGGLES = [
  { key: "reminders_90", label: "90-day reassessment invitations", hint: "Gentle email about three months after a completed discovery, only for members who opted in." },
  { key: "reminders_180", label: "180-day reassessment invitations", hint: "A second quiet check-in around six months, same opt-in rules." },
  { key: "report_ready", label: "Report-ready notifications", hint: "Tells a member their private results are ready right after they finish." },
  { key: "welcome_email", label: "Welcome emails", hint: "A warm hello when someone creates an account." },
];

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "";

export default function AdminMessaging() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/admin/communications").then(({ data }) => setData(data));

  useEffect(() => {
    document.title = "Messaging | Rooted Healing Admin";
    load().catch(() => setData(null));
  }, []);

  const toggle = async (key) => {
    const next = { ...data.settings, [key]: !data.settings[key] };
    setData((d) => ({ ...d, settings: next }));
    setSaving(true);
    try {
      await api.put("/admin/communications", { values: { [key]: next[key] } });
      toast.success("Setting saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      load();
    }
    setSaving(false);
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data: res } = await api.post("/admin/communications/test");
      toast.success(`Test message sent to ${res.to}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Test email failed."));
      load();
    }
    setTesting(false);
  };

  if (!data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-forest" />
      </div>
    );
  }

  return (
    <div data-testid="admin-messaging">
      <h1 className="font-serif text-3xl text-forest">Messaging & Reminders</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-taupe">
        Transactional email through {data.provider}. Messages never contain wellness answers or
        report content, only links back to the member's private account. Members control their
        own reminder and newsletter consents.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="rounded-[1.75rem] border border-forest/10 bg-white/75 p-6">
          <h2 className="font-serif text-xl text-forest">Campaign switches</h2>
          <div className="mt-4 flex flex-col gap-4">
            {TOGGLES.map((t) => (
              <div key={t.key} className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-forest">{t.label}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-taupe">{t.hint}</p>
                </div>
                <button
                  data-testid={`comm-toggle-${t.key}`}
                  onClick={() => toggle(t.key)}
                  disabled={saving}
                  role="switch"
                  aria-checked={data.settings[t.key]}
                  className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-300 ${
                    data.settings[t.key] ? "bg-forest" : "bg-charcoal/20"
                  }`}
                >
                  <span
                    className={`absolute top-1 h-5 w-5 rounded-full bg-ivory shadow transition-all duration-300 ${
                      data.settings[t.key] ? "left-6" : "left-1"
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-6 border-t border-forest/10 pt-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-forest">Connection test</p>
                <p className="mt-0.5 text-xs text-taupe">
                  Sends a test message to your own admin address.
                </p>
              </div>
              <button
                data-testid="comm-test-email"
                onClick={sendTest}
                disabled={testing}
                className="inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-xs font-semibold text-ivory transition-colors hover:bg-forest-deep disabled:opacity-50"
              >
                {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Send test email
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-[1.75rem] border border-forest/10 bg-white/75 p-6">
          <h2 className="font-serif text-xl text-forest">Delivery summary</h2>
          <div className="mt-4 grid grid-cols-3 gap-3">
            {[
              { label: "Emails sent", value: data.stats.emails_sent },
              { label: "Failed", value: data.stats.emails_failed },
              { label: "Reminders sent", value: data.stats.reminders_sent },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl bg-sage/60 p-4 text-center">
                <p className="font-serif text-2xl text-forest">{s.value}</p>
                <p className="mt-1 text-[11px] uppercase tracking-wide text-taupe">{s.label}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs leading-relaxed text-taupe">
            Duplicate prevention is built in: each member receives a given reminder type only
            once, and never more than one reminder in 30 days.
          </p>
        </div>
      </div>

      <div className="mt-6 rounded-[1.75rem] border border-forest/10 bg-white/75 p-6">
        <div className="flex items-center gap-2">
          <MailCheck className="h-4 w-4 text-forest" />
          <h2 className="font-serif text-xl text-forest">Recent delivery activity</h2>
        </div>
        <div className="mt-4 flex max-h-96 flex-col gap-2 overflow-y-auto">
          {data.log.length === 0 && (
            <p className="py-6 text-center text-sm text-taupe">No email activity yet.</p>
          )}
          {data.log.map((e, i) => (
            <div
              key={i}
              data-testid={`email-log-${i}`}
              className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-forest/8 px-4 py-3 text-sm"
            >
              <div>
                <p className="font-semibold text-forest">{e.subject}</p>
                <p className="text-xs text-charcoal/70">
                  to {e.to} · {e.category}
                </p>
              </div>
              <div className="text-right text-xs">
                <span
                  className={`rounded-full px-2.5 py-1 font-bold uppercase tracking-wide ${
                    e.status === "sent" ? "bg-sage text-forest" : "bg-red-900/10 text-red-900/70"
                  }`}
                >
                  {e.status}
                </span>
                <p className="mt-1 text-taupe">{fmt(e.at)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
